import React, { Component } from 'react';

import { Button, message } from 'antd';
import Webcam from 'react-webcam';
import '@tensorflow/tfjs-backend-webgl';
import cx from 'classnames';
import * as blazeface from '@tensorflow-models/blazeface';

import { isMobileOrTablet } from '../../utils/platform';
import { DESKTOP, MOBILE } from '../types/device';

import styles from './faceDetection.module.scss';

enum FaceState {
  NOFACE,
  TOO_CLOSE,
  TOO_FAR,
  GOOD_DISTANCE,
}
interface WebcamTestState {
  isRecording: boolean;
  isCapturing: boolean;
  videoRecorded: Blob[];
  capturedImage: string | null;
  webcamOpened: boolean;
  finishedRecording: boolean;
  devices: { name: string; deviceId: string }[];
  selectedDeviceId: string;
  videoWidth?: number;
  videoHeight?: number;
  faceState: FaceState;
  percentageDetected?: number;
  probabilityDetected: number;
  minimumProbability: number;
}

const DISTANCES = {
  DESKTOP: {
    TOO_CLOSE: 0.4,
    TOO_FAR: 0.2,
  },
  MOBILE: {
    TOO_CLOSE: 0.8,
    TOO_FAR: 0.4,
  },
};

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface IWebcamTestProps {}

const ASPECT_RATIO = 4 / 3; // 4:3 aspect ratio

export default class WebcamTestFaceDetection extends Component<IWebcamTestProps, WebcamTestState> {
  private webcamRef: React.RefObject<Webcam>;
  private mediaRecorderRef: MediaRecorder | null = null;
  private model: blazeface.BlazeFaceModel | null = null;
  private intervalId: NodeJS.Timeout | null = null;
  private canvasOverlay: React.RefObject<HTMLCanvasElement>;

  constructor(props: any) {
    super(props);
    this.webcamRef = React.createRef();
    this.canvasOverlay = React.createRef();
    this.capturePhoto = this.capturePhoto.bind(this);
    this.captureVideo = this.captureVideo.bind(this);
    this.onDataAvailable = this.onDataAvailable.bind(this);
    this.stopVideo = this.stopVideo.bind(this);
    this.openCapturePhoto = this.openCapturePhoto.bind(this);
    this.openCaptureVideo = this.openCaptureVideo.bind(this);
    this.closeWebcam = this.closeWebcam.bind(this);
    this.switchCamera = this.switchCamera.bind(this);
    this.detectFace = this.detectFace.bind(this);
    this.updateDimensions = this.updateDimensions.bind(this);
    this.state = {
      isCapturing: false,
      isRecording: false,
      videoRecorded: [],
      capturedImage: null,
      webcamOpened: false,
      finishedRecording: false,
      devices: [],
      selectedDeviceId: isMobileOrTablet() ? 'user' : '',
      faceState: FaceState.NOFACE,
      probabilityDetected: 0,
      minimumProbability: 0.8,
    };
  }

  private setVideoSize() {
    const { innerHeight, innerWidth } = window;
    const maxWidth = innerWidth - 40; // 20px padding on each side
    const height = innerWidth / ASPECT_RATIO;
    if (height < innerHeight - 200) {
      this.setState({
        videoHeight: height,
        videoWidth: maxWidth,
      });
      return;
    }
    const maxHeight = innerHeight - 200; // 200px for controls
    const width = innerHeight * ASPECT_RATIO;
    this.setState({
      videoHeight: maxHeight,
      videoWidth: width,
    });
  }

  componentDidMount(): void {
    window.addEventListener('resize', this.updateDimensions);
    this.setVideoSize();
  }
  async componentDidUpdate() {
    const { devices } = this.state;
    if (!isMobileOrTablet() && devices.length === 0) {
      const detectedVideoDevices = (await navigator.mediaDevices.enumerateDevices()).filter(
        ({ kind }) => kind === 'videoinput'
      );
      this.setState({
        devices: detectedVideoDevices.map(({ deviceId, label }) => ({
          name: label,
          deviceId,
        })),
        selectedDeviceId: detectedVideoDevices[0]?.deviceId || '',
      });
    }
  }
  componentWillUnmount(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    window.removeEventListener('resize', this.updateDimensions);
  }

  private updateDimensions() {
    this.setVideoSize();
  }

  private switchCamera() {
    const { selectedDeviceId } = this.state;
    this.setState({ selectedDeviceId: selectedDeviceId === 'user' ? 'environment' : 'user' });
  }

  private onDataAvailable({ data }: BlobEvent) {
    const { finishedRecording } = this.state;
    if (data.size > 0 && finishedRecording) {
      this.setState((prevState) => ({
        videoRecorded: [data],
        capturedImage: null,
        finishedRecording: false,
      }));
      message.success('Capture successful');
    }
  }

  private closeWebcam() {
    this.setState(
      {
        isCapturing: false,
        isRecording: false,
        capturedImage: null,
        videoRecorded: [],
        webcamOpened: false,
        finishedRecording: false,
      },
      () => {
        this.mediaRecorderRef?.stop();
      }
    );
  }

  private async openCapturePhoto() {
    await this.loadModelAndStartDetectingFaces();
    this.setState({
      isCapturing: true,
      isRecording: false,
      capturedImage: null,
      videoRecorded: [],
      webcamOpened: true,
    });
  }

  private async detectFace() {
    if (!this.model || !this.webcamRef.current?.video || !this.canvasOverlay.current) {
      return;
    }
    if (!this.webcamRef.current?.video.videoHeight || !this.webcamRef.current?.video.videoWidth) {
      return;
    }
    const canvas = this.canvasOverlay.current;
    const predictions = await this.model.estimateFaces(this.webcamRef.current.video, false, false, true);
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }
    const { minimumProbability } = this.state;
    const { videoHeight, videoWidth } = this.webcamRef.current?.video ?? {};
    const { height, width } = canvas;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    this.analyzeFace(predictions);
    predictions.forEach((prediction) => {
      if (!prediction.probability || !Array.isArray(prediction.probability)) {
        return;
      }
      if ((prediction.probability[0] ?? 0) < minimumProbability) {
        return;
      }
      ctx.beginPath();
      ctx.lineWidth = 4;
      ctx.strokeStyle = 'red';
      ctx.rect(
        prediction.topLeft[0] * (width / videoWidth),
        prediction.topLeft[1] * (height / videoHeight),
        (prediction.bottomRight[0] - prediction.topLeft[0]) * (width / videoWidth),
        (prediction.bottomRight[1] - prediction.topLeft[1]) * (height / videoHeight)
      );
      ctx.stroke();
      if (Array.isArray(prediction.landmarks)) {
        prediction.landmarks?.forEach((landmark) => {
          ctx.fillStyle = 'red';
          ctx.beginPath();
          ctx.arc(landmark[0] * (width / videoWidth), landmark[1] * (height / videoHeight), 5, 0, 2 * Math.PI);
          ctx.fill();
        });
      }
    });
  }
  private analyzeFace(predictions: blazeface.NormalizedFace[]) {
    const { minimumProbability } = this.state;
    if (predictions.length === 0) {
      this.setState({ faceState: FaceState.NOFACE, probabilityDetected: 0 });
      return;
    }
    let bestPredictionIndex = -1;
    let bestPredictionProbability = 0;
    predictions.forEach((prediction, index) => {
      if (!prediction.probability || !Array.isArray(prediction.probability)) {
        return;
      }
      if (prediction.probability[0] > bestPredictionProbability) {
        bestPredictionProbability = prediction.probability[0];
        bestPredictionIndex = index;
      }
    });
    const bestPrediction = predictions[bestPredictionIndex];
    if (bestPredictionProbability < minimumProbability) {
      this.setState({
        probabilityDetected: bestPredictionProbability,
      });
      return;
    }
    const { videoWidth = 0, videoHeight = 0 } = this.state;
    const faceWidth = bestPrediction.bottomRight[0] - bestPrediction.topLeft[0];
    const faceHeight = bestPrediction.bottomRight[1] - bestPrediction.topLeft[1];
    const faceSize = Math.sqrt(faceWidth * faceWidth + faceHeight * faceHeight);
    const videoSize = Math.sqrt(videoWidth * videoWidth + videoHeight * videoHeight);
    const distances = isMobileOrTablet() ? DISTANCES[MOBILE] : DISTANCES[DESKTOP];
    const percentageDetected = faceSize / videoSize;
    if (percentageDetected > distances.TOO_CLOSE) {
      this.setState({
        faceState: FaceState.TOO_CLOSE,
        probabilityDetected: bestPredictionProbability,
      });
    } else if (percentageDetected < distances.TOO_FAR) {
      this.setState({
        faceState: FaceState.TOO_FAR,
        probabilityDetected: bestPredictionProbability,
      });
    } else {
      this.setState({
        faceState: FaceState.GOOD_DISTANCE,
        probabilityDetected: bestPredictionProbability,
      });
    }
    this.setState({ percentageDetected });
  }

  private async loadModelAndStartDetectingFaces() {
    this.model = await blazeface.load();

    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    this.intervalId = setInterval(() => {
      this.detectFace();
    }, 100);
  }

  private async openCaptureVideo() {
    await this.loadModelAndStartDetectingFaces();
    this.setState(
      {
        webcamOpened: true,
      },
      () => {
        this.webcamRef.current?.video?.addEventListener('loadeddata', () => {
          if (!this.webcamRef.current?.video) {
            return;
          }
          this.setState({
            isCapturing: false,
            isRecording: false,
            capturedImage: null,
            videoRecorded: [],
          });
        });
      }
    );
  }

  private capturePhoto() {
    const imageSrc = this.webcamRef.current?.getScreenshot();
    if (imageSrc) {
      message.success('Capture successful');
      this.setState({
        capturedImage: imageSrc,
        videoRecorded: [],
        webcamOpened: false,
      });
    } else {
      message.error('Capture failed');
    }
    this.setState({ isCapturing: false });
  }

  private stopVideo() {
    if (!this.mediaRecorderRef) {
      return;
    }
    this.setState({ isRecording: false, webcamOpened: false, finishedRecording: true }, () => {
      this.mediaRecorderRef?.stop();
    });
  }

  private getSupportedMimeTypes() {
    const mimeTypes = [
      'video/webm; codecs=vp9',
      'video/webm; codecs=vp9,opus',
      'video/webm; codecs=vp8,opus',
      'video/webm; codecs=vp8',
      'video/webm',
      'video/mp4; codecs=h264,aac',
      'video/mp4; codecs=h264',
      'video/mp4',
      'video/mov; codecs=h264,aac',
      'video/mov; codecs=h264',
      'video/mov',
    ];
    const supportedMimeTypes = mimeTypes.filter((mimeType) => MediaRecorder.isTypeSupported(mimeType));
    if (supportedMimeTypes.length === 0) {
      message.error('No supported mime types found');
      return null;
    }
    return supportedMimeTypes[0];
  }

  private captureVideo() {
    this.setState({ isRecording: true, capturedImage: null, videoRecorded: [] }, () => {
      setTimeout(() => {
        if (!this.webcamRef.current || !this.webcamRef.current.stream) {
          return;
        }
        const mimeType = this.getSupportedMimeTypes();
        if (!mimeType) {
          this.setState({ isRecording: false, capturedImage: null, videoRecorded: [] });
          return;
        }
        this.mediaRecorderRef = new MediaRecorder(this.webcamRef.current.stream, {
          mimeType,
        });
        this.mediaRecorderRef.addEventListener('dataavailable', this.onDataAvailable);
        this.mediaRecorderRef.start();
      }, 500);
    });
  }

  private renderVideoButtons() {
    const { isCapturing, isRecording } = this.state;
    if (isCapturing) {
      return null;
    }

    if (isRecording) {
      return (
        <Button type="primary" onClick={this.stopVideo}>
          Stop video
        </Button>
      );
    }
    return (
      <Button type="primary" onClick={this.captureVideo}>
        {this.state.videoRecorded.length === 0 ? 'Start recording' : 'Record again'}
      </Button>
    );
  }
  private renderPhotoButtons() {
    const { isCapturing, isRecording } = this.state;
    if (isRecording || !isCapturing) {
      return null;
    }
    return (
      <Button type="primary" onClick={this.capturePhoto}>
        Capture
      </Button>
    );
  }

  private renderPreview() {
    const { videoRecorded, capturedImage, isRecording } = this.state;
    if ((!videoRecorded.length && !capturedImage) || isRecording) {
      return null;
    }
    if (capturedImage) {
      return (
        <div>
          <h2>Preview</h2>
          <img className={styles.photoPreview} src={capturedImage} alt="Captured" />
        </div>
      );
    }
    const videoBlob = new Blob(videoRecorded, { type: this.getSupportedMimeTypes() ?? 'video/webm' });
    const videoUrl = URL.createObjectURL(videoBlob);
    return (
      <div>
        <h2>Preview</h2>
        <video className={styles.videoPreview} controls src={videoUrl} />
      </div>
    );
  }

  private renderProbabilityDetectionSelector() {
    const { minimumProbability } = this.state;
    const percentages = [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1];
    return (
      <select
        className={styles.probabilityDetectionSelector}
        value={`${minimumProbability}`}
        onChange={(e) => {
          this.setState({ minimumProbability: +e.target.value });
        }}
      >
        {percentages.map((percentage) => (
          <option key={percentage} value={percentage}>
            {percentage * 100}%
          </option>
        ))}
      </select>
    );
  }
  private renderCameraSelector() {
    const { devices, selectedDeviceId } = this.state;
    if (devices.length === 0) {
      return <Button onClick={this.switchCamera}>Switch camera</Button>;
    }
    return (
      <select
        className={styles.cameraSelector}
        value={selectedDeviceId}
        onChange={(e) => {
          this.setState({ selectedDeviceId: e.target.value });
        }}
      >
        {devices.map(({ name, deviceId }) => (
          <option key={deviceId} value={deviceId}>
            {name}
          </option>
        ))}
      </select>
    );
  }

  private renderFaceState() {
    const { faceState, percentageDetected = 0, probabilityDetected, minimumProbability } = this.state;
    let message = '';
    let className = '';
    switch (faceState) {
      case FaceState.NOFACE:
        message = 'No face detected';
        className = styles.noFace;
        break;
      case FaceState.TOO_CLOSE:
        message = 'Too close';
        className = styles.tooClose;
        break;
      case FaceState.TOO_FAR:
        message = 'Too far';
        className = styles.tooFar;
        break;
      case FaceState.GOOD_DISTANCE:
        message = 'Good distance';
        className = styles.goodDistance;
        break;
      default:
        message = '';
        className = '';
        break;
    }
    const probabilityClassName = probabilityDetected < minimumProbability ? styles.noFace : styles.veryProbable;
    return (
      <div className={styles.faceStateContainer}>
        <div className={cx(styles.faceState, probabilityClassName)}>
          Probability: %{(probabilityDetected * 100).toFixed(2)}
        </div>
        <div className={cx(styles.faceState, className)}>
          {message} ({(percentageDetected * 100).toFixed(2)}%)
        </div>
      </div>
    );
  }

  private renderWebcam() {
    const { webcamOpened, selectedDeviceId, videoHeight, videoWidth } = this.state;
    if (!webcamOpened) {
      return null;
    }
    let videoConstraints: MediaTrackConstraints = {
      deviceId: selectedDeviceId,
      height: videoHeight,
      width: videoWidth,
      aspectRatio: {
        ideal: ASPECT_RATIO,
        exact: ASPECT_RATIO,
      },
    };
    if (isMobileOrTablet()) {
      if (selectedDeviceId === 'user') {
        videoConstraints = {
          facingMode: 'user',
        };
      } else {
        videoConstraints = {
          facingMode: { exact: 'environment' },
        };
      }
    }
    return (
      <>
        <div className={styles.videoContainer}>
          <Webcam
            className={styles.webcam}
            audio={false}
            ref={this.webcamRef}
            screenshotFormat="image/jpeg"
            download
            videoConstraints={videoConstraints}
            width={videoWidth}
            height={videoHeight}
          />
          <div className={styles.overlay}>
            <canvas width={videoWidth} height={videoHeight} ref={this.canvasOverlay} className={styles.canvas} />
          </div>
        </div>
        {this.renderFaceState()}
        <div className={styles.selectors}>
          {this.renderCameraSelector()}
          {this.renderProbabilityDetectionSelector()}
        </div>
      </>
    );
  }

  private renderCancelButton() {
    return (
      <Button onClick={this.closeWebcam} type="default">
        Cancel
      </Button>
    );
  }

  private renderMainButtons() {
    const { webcamOpened } = this.state;
    if (!webcamOpened) {
      return (
        <>
          <Button onClick={this.openCapturePhoto} type="primary">
            Photo
          </Button>
          <Button onClick={this.openCaptureVideo} type="primary">
            Video
          </Button>
        </>
      );
    }
    return (
      <>
        {this.renderCancelButton()}
        {this.renderPhotoButtons()}
        {this.renderVideoButtons()}
      </>
    );
  }

  render() {
    return (
      <div className={styles.container}>
        {this.renderWebcam()}
        {this.renderPreview()}
        <div className={styles.buttons}>{this.renderMainButtons()}</div>
      </div>
    );
  }
}
